import { ForecastModel } from '@app/common/static-data/models';
import { VariableSelector } from './VariableSelector';
import { TopBar } from './TopBar';
import { ForecastChartContainer } from './ForecastChartContainer';
import { PlottableForecast, VariableId, ModelId, Location } from '../types';
import { BaseForecastModelWithAvailability } from '@app/common/static-data/models';

interface ForecastContentProps {
  loading: boolean;
  forecastData: Record<ModelId, PlottableForecast>;
  selectedParam: VariableId;
  selectedModels: ForecastModel[];
  endHour: number;
  onParamChange: (param: VariableId) => void;
  favoriteVariables?: Set<string>;
  // Location props
  location?: Location;
  onLocationChange?: (location: Location) => void;
  favoriteLocations?: Location[];
  // Panel control
  onOpenPanel?: () => void;
  onOpenPanelWithLocation?: () => void;
  onOpenPanelWithModel?: () => void;
  // Model props
  availableModels?: BaseForecastModelWithAvailability[];
  favoriteModels?: Set<string>;
  onModelsChange?: (models: ForecastModel[]) => void;
  // Model color props
  customModelColors: Record<string, string>;
  isPanelOpen?: boolean;
  onSettingsClick?: () => void;
}

export function ForecastContent({
  loading,
  forecastData,
  selectedParam,
  selectedModels,
  endHour,
  onParamChange,
  favoriteVariables = new Set(),
  location,
  onLocationChange,
  favoriteLocations = [],
  onOpenPanel,
  onOpenPanelWithLocation,
  onOpenPanelWithModel,
  availableModels = [],
  favoriteModels = new Set(),
  onModelsChange,
  customModelColors,
  isPanelOpen = false,
  onSettingsClick,
}: ForecastContentProps) {
  const handleModelToggle = (modelId: ForecastModel) => {
    if (!onModelsChange) return;

    if (selectedModels.includes(modelId)) {
      onModelsChange(selectedModels.filter((id) => id !== modelId));
    } else {
      onModelsChange([...selectedModels, modelId]);
    }
  };

  // Add extra top padding so content does not sit underneath the absolute VariableSelector
  const contentTopPaddingClass = selectedParam === 'weather_summary' ? 'pt-16' : 'pt-0';

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-theme-dark-bg flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <TopBar
          location={location}
          onLocationChange={onLocationChange}
          favoriteLocations={favoriteLocations}
          onOpenPanelWithLocation={onOpenPanelWithLocation}
          loading={loading}
          availableModels={availableModels}
          favoriteModels={favoriteModels}
          selectedModels={selectedModels}
          onModelToggle={handleModelToggle}
          onOpenPanel={onOpenPanelWithModel}
          onSettingsClick={onSettingsClick}
          isPanelOpen={isPanelOpen}
        />

        {/* Chart Content */}
        <div className={`p-4 ${contentTopPaddingClass} relative flex-1 flex flex-col min-h-0`}>
          {/* Variable Selection - Top Left (ensure above summary header) */}
          <div className="absolute top-4 left-4 z-40">
            <VariableSelector
              selectedParam={selectedParam}
              favoriteVariables={favoriteVariables}
              onParamChange={onParamChange}
              onOpenPanel={onOpenPanel}
              loading={loading}
            />
          </div>

          {/* Chart Container */}
          <ForecastChartContainer
            loading={loading}
            forecastData={forecastData}
            selectedParam={selectedParam}
            selectedModels={selectedModels}
            endHour={endHour}
            location={location || { lat: 0, lon: 0, name: 'Unknown Location' }}
            customModelColors={customModelColors}
          />
        </div>
      </div>
    </div>
  );
}
